<template>
  <section class="sec sec2" id='assumptions-form'>
  <div class="container">
    <article class="content text-center mb-50 ">
      <h2>Assumptions</h2>
      <p style="font-size: 15px;">We made the following assumptions in calculating the pension. Change them to see how the cost of a pension would increase or decrease. The assumptions include not only the cost of pension, but also overhead expenses associated with setting up and operating a company to administer the pensions, in order to provide a more realistic calculation of costs. <br class="visible-md visible-lg">
      </p>
    </article>
    <div class="row">
      <div class="col-xs-12 col-sm-11 col-md-10 mx-auto">
        <form class="formCont" v-on:submit.prevent="resetValues">
          <p class="form-field form-field-assumption clearfix">
            <label>
              Interest Rate (after inflation)
            </label>
            <PercentageInput v-model="interest"/>
          </p>

          <p class="form-field form-field-assumption clearfix">
            <label>
              Reserve Capital (percent of expected payment)<sup
              class="fa fa-question"
              style="font-size: 12px;"
                v-tooltip="'Reserve capital will be required if the pension is provided by a private sector company (to ensure the pensioners will receive their annual payments even if they live longer than expected) - perhaps 15%. A government provider of pensions may not explicitly put aside reserve capital against the same risk.'" />
            </label>
            <PercentageInput v-model="reserveCapRate"/>
          </p>


          <p class="form-field form-field-assumption clearfix">
            <label for="interest">
              Upfront Admin Costs (multiple of annual payment)<sup
              class="fa fa-question"
              style="font-size: 12px;"
                v-tooltip="'As a fraction of annual payments. This cost is included to present a more realistic picture of running this business.'" />
            </label>
            <RateInput v-model="salesPercentCost" />
          </p>


          <p class="form-field form-field-assumption clearfix">
            <label for="interest">Ongoing Admin Costs (percent of annual payment)<sup
                class="fa fa-question"
                style="font-size: 12px;"
                v-tooltip="'Based on annuity industry estimate. This cost is included to present a more realistic picture of running this business. A reduction in admin costs of 75% is expected until payments start being made.'" />
            </label>
            <PercentageInput v-model="adminCostDuringPayments"/>
          </p>
          <hr style="margin-right: 25px;">

          <div class="col-md-12" style="padding-left: 0; margin-top: 10px;">
            <label class="custom-radio">
              <input v-model='usePercentOfGDP' type="radio" name="" value="percent"/>
              <span class="label-text">Percent of GDP {{format(countryGDP)}}</span>
            </label>
          </div>
          <div class="col-md-12" style="padding-left: 0; margin-top: 10px;">
            <label class="custom-radio">
              <input v-model='usePercentOfGDP' type="radio" name="" value="amount" />
              <span class="label-text">Amount in dollars</span>
            </label>
          </div>

          <div v-if="yearlyAmountAs == 'percent'" class="form-group form-row">
            <p class="form-field form-field-assumption clearfix">
              <label for="interest">Payments (% of GDP per capita)<sup
                class="fa fa-question"
                style="font-size: 12px"
                v-tooltip="'Default assumes a retiree will need 20% of annual GDP per capita to cover basic costs. Adjust this up or down for your circumstance.'" />
              </label>
              <PercentageInput v-model="annualPaymentPercent"/>
            </p>
          </div>

          <div v-else class="form-group form-row">
            <p class="form-field form-field-assumption clearfix">
              <label for="apa">Payments ($)</label>
              <span class="field assumption-field">
                <input
                  v-model="annualPaymentAmount"
                  @keypress="onlyNumber"
                  name="apa"
                  id="apa"
                  class="form-control form-control-assumptions" />
              </span>
            </p>
          </div>
          <hr style="margin-right: 25px;">

          <div class="col-md-10" style="padding-left: 0; margin-top: 10px;">
            <ul class="form-field-radios list-inline">
              <li class="form-field" style="margin: 0px; padding: 0px;">
                <label class="custom-checkbox">
                  <input v-model='useLifeExpectancy' type="checkbox" name="" value="percent"  />
                  <span class="label-text">Use WHO Life Expectancy</span>
                </label>

              </li>
            </ul>
          </div>

          <div class="form-group form-row">
            <p class="form-field form-field-assumption clearfix">
              <label for="interest">Life Expectancy (years)<sup
                class="fa fa-question"
                style="font-size: 12px"
                v-tooltip="" />
              </label>
              <span v-if="useLifeExpectancy" class="field assumption-field">
                <input
                  :value="$store.getters.lifeExpectancy"
                  name="apa"
                  id="apa"
                  disabled
                  class="form-control form-control-assumptions" />
              </span>
              <span v-else class="field assumption-field">
                <input
                v-model="ageOfDeath"
                @keypress="onlyNumber"
                name="apa"
                id="apa"
                class="form-control form-control-assumptions" />
              </span>
            </p>
          </div>

          <div class="form-group">
            <div class="col-md-10"></div>
            <div class="col-md-2">
              <button
              id="singlebutton"
              style="width: 100%;"
              name="singlebutton"
              class="btn btn-primary btn-submit">RESET</button>
            </div>
        </div>

          <br>
        </form>
      </div>
    </div>
    <div style="padding-top: 50px;" class='row'>
      <Breakdown />
    </div>
  </div>
  </section>
</template>

<script>
import annuities from '../js/annuityCalc.js'
import { mapGetters } from 'vuex'
import accounting from 'accounting'
import { minValue } from 'vuelidate/lib/validators'
import Breakdown from './Breakdown.vue'
import PercentageInput from './PercentageInput'
import RateInput from './RateInput'

export default {
  name: 'Inputs',
  components: {
    'Breakdown': Breakdown,
    'PercentageInput': PercentageInput,
    'RateInput': RateInput
  },
  data: function () {
    return {
      dollarAmountInt: 1000
    }
  },
  computed: {
    ...mapGetters([
      'result',
      'salesPercentCost',
      'adminCostDuringPayments',
      'yearlyAmountAs',
      'yearlyPaymentInfo',
      'countryGDP',
    ]),
    useLifeExpectancy: {
      get () {
        return this.$store.getters.useLifeExpectancy
      },
      set (value) {
        this.$store.commit('setUseLifeExpectancy', value)
        this.submitData()
      },
    },
    ageOfDeath: {
      get () {
        return this.$store.getters.lifeExpectancy
      },
      set (value) {
        this.$store.commit('setAgeOfDeath', value)
        this.submitData()
      },
    },
    usePercentOfGDP: {
      get () {
        return this.yearlyAmountAs
      },
      set (value) {
        this.$store.commit('setYearlyAmountAs', value)
        this.submitData()
      }
    },
    interest: {
      get () {
        return 100*this.$store.getters.interest
      },
      set (value) {
        this.$store.commit('setInterest', value/100)
        this.submitData()
      }
    },
    reserveCapRate: {
      get () {
        return 100*this.$store.getters.reserveCapRate
      },
      set (value) {
        this.$store.commit('setReserveCapRate', value/100)
        this.submitData()
      }
    },
    annualPaymentPercent: {
      get () {
        return 100*this.$store.getters.yearlyPaymentInfo.percentOfGDP
      },
      set (value) {
        this.$store.commit('setPercentOfGDP', value/100)
        this.submitData()
      }
    },
    annualPaymentAmount: {
      get () {
        var amount = this.$store.getters.yearlyPaymentInfo.annualPayments
        return accounting.formatMoney(amount, "$", 0)
      },
      set (value) {
        var amount = accounting.unformat(value.replace(/[^0-9]/g, ''))
        this.dollarAmountInt = amount
        this.$store.commit('setAnnualPaymentAmount', amount)
        this.submitData()
      }
    },
    salesPercentCost: {
      get () {
        return this.$store.getters.salesPercentCost
      },
      set (value) {
        this.$store.commit('setSalesPercentCost', value)
        this.submitData()
      }
    },
    adminCostDuringPayments: {
      get () {
        return 100*this.$store.getters.adminCostDuringPayments
      },
      set (value) {
        this.$store.commit('setAdminCostDuringPayments', value/100)
        this.submitData()
      }
    }
  },
  methods: {
    submitData(){
      annuities.calc({ ctx: this })
    },
    resetValues(){
      this.$store.commit('setInterest', 0.03)
      this.$store.commit('setPercentOfGDP', 0.2)
      this.$store.commit('setAnnualPaymentAmount', 1000)
      this.$store.commit('setSalesPercentCost', 1.5)
      this.$store.commit('setAdminCostDuringPayments', 0.2)
      this.$store.commit('setReserveCapRate', 0)
      this.$store.commit('setSelectedAgePaymentsStart', 65)
      annuities.calc({ ctx: this })
    },
    format(value){
      if (value == null) return ''
      return `(${accounting.formatMoney(value, "$", 0)})`
    },
    onlyNumber ($event) {
       let keyCode = ($event.keyCode ? $event.keyCode : $event.which);
       if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { // 46 is dot
          $event.preventDefault();
       }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h3 {
  margin: 40px 0 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}

.calc-form {
  border: 1px black solid;
  border-radius: 10px;
  background-color: white;
  /* box-shadow: 3px 6px #888888; */
  padding: 50px;
  padding-bottom: 40px;
  opacity: 0.8;
}

.btn-submit {
  width: 50%;
}

.form-label-left {
  text-align: left;
}

.field .assumption-field {
  margin: 0 0 0 400px;
}

.form-field-assumption label{
  width: 400px;
}

.form-field .form-field-assumption {
    margin: 0 0 0;
}

.form-field {
  margin: 0 0 20px;
}

.form-control {
  width: 47%;
}

.form-control .form-control-assumptions {
  width: 47%;
}
</style>
